import type { Product } from "@jasper/client-models";
import { getGroupTeeth, sortByFirstTooth } from "./productGroupTeeth";
import {
  isBulkCompatible,
  isArchScopedProductType,
  isBulkCreationSupportedForProductType,
} from "./registry";

/**
 * A group of products configured together, seen from its leader.
 *
 * `groupProducts` and `groupTeeth` describe the whole group, leader included.
 * The followers are what the `?bulk=` param carries, the leader living in the
 * path instead. `configuredProducts` is the group as the steps receive it: the
 * leader first, then its followers by first tooth.
 */
export type BulkProductGroup<TProduct extends Product> = {
  isBulkMode: boolean;
  isBulkSupported: boolean;
  bulkProductFollowerIds: string[];
  groupProducts: TProduct[];
  groupTeeth: number[];
  configuredProducts: TProduct[];
};

/**
 * Resolves the requested product ids against the loaded order, dropping those
 * that no longer match a product of the leader's type so a group never outlives
 * its products. A leader whose type does not support bulk is always alone.
 *
 * An arch-scoped type ignores the request and takes every compatible product of
 * the order: its step shows one selection covering all of them, so a group built
 * from `?bulk=` alone would let the step submit an arch held by a product it does
 * not know, and the submit would create a second one on that arch.
 */
export function resolveBulkProductGroup<TProduct extends Product>({
  leaderProduct,
  products,
  requestedProductIds,
}: {
  leaderProduct: TProduct | undefined;
  products: ReadonlyArray<TProduct | null | undefined>;
  requestedProductIds: ReadonlyArray<string>;
}): BulkProductGroup<TProduct> {
  const isBulkSupported = leaderProduct
    ? isBulkCreationSupportedForProductType(leaderProduct.type)
    : false;

  const followerProducts =
    leaderProduct && isBulkSupported
      ? resolveFollowerProducts({
          leaderProduct,
          products,
          requestedProductIds,
        })
      : [];

  const groupProducts = leaderProduct
    ? sortByFirstTooth([leaderProduct, ...followerProducts])
    : [...followerProducts];

  return {
    isBulkMode: followerProducts.length > 0,
    isBulkSupported,
    bulkProductFollowerIds: followerProducts.map(product => product.id),
    groupProducts,
    groupTeeth: getGroupTeeth(groupProducts),
    configuredProducts: leaderProduct
      ? [leaderProduct, ...followerProducts]
      : followerProducts,
  };
}

const resolveFollowerProducts = <TProduct extends Product>({
  leaderProduct,
  products,
  requestedProductIds,
}: {
  leaderProduct: TProduct;
  products: ReadonlyArray<TProduct | null | undefined>;
  requestedProductIds: ReadonlyArray<string>;
}): TProduct[] => {
  const loadedProducts = products.filter((product): product is TProduct =>
    Boolean(product)
  );

  const isCompatibleFollower = (product: TProduct) =>
    product.id !== leaderProduct.id && isBulkCompatible(leaderProduct, product);

  if (isArchScopedProductType(leaderProduct.type)) {
    return sortByFirstTooth(loadedProducts.filter(isCompatibleFollower));
  }

  const productsById = new Map(
    loadedProducts.map(product => [product.id, product])
  );

  return sortByFirstTooth(
    [...new Set(requestedProductIds)]
      .map(productId => productsById.get(productId))
      .filter(
        (product): product is TProduct =>
          !!product && isCompatibleFollower(product)
      )
  );
};
